import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { Printer, ShieldCheck, MapPin, Mail, Globe } from "lucide-react";

/** Digital + printable Associate ID Card.
 *  Referenced from reference site's "friendly ID card" — includes QR for easy verification.
 *  Design uses existing royal-blue + gold theme (no radical color change per user note).
 */
export default function IdCard() {
  const { user } = useAuth();
  const qrData = encodeURIComponent(
    `Samara Wiz IBA\nID: ${user?.user_code}\nName: ${user?.name || ""}\nVerify: https://samarawiz.com/verify/${user?.user_code || ""}`
  );
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${qrData}&color=0B3D91&bgcolor=ffffff`;

  return (
    <DashboardLayout title="Associate ID Card" subtitle="Your official Samara Wiz identity — print or share">
      <div className="max-w-4xl mx-auto">
        {/* Actions bar (hidden when printing) */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <div className="text-xs text-navy/60">
            Show this card at company events, partner meetings and franchise stores.
          </div>
          <button onClick={() => window.print()} data-testid="idcard-print" className="px-5 py-2.5 bg-royal text-white hover:bg-royal-deep rounded-sm inline-flex items-center gap-2 text-sm">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>

        {/* THE CARD */}
        <div id="idcard-print-area" className="mx-auto max-w-[720px] bg-white border-2 border-royal rounded-sm shadow-2xl overflow-hidden">
          {/* Top band */}
          <div className="bg-gradient-to-r from-royal via-royal-deep to-royal text-white px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 grid place-items-center bg-gold text-royal rounded-sm font-serif font-bold text-lg">S</div>
              <div>
                <div className="font-serif text-xl leading-none">SAMARA WIZ</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-gold mt-1">Health · Education · Beauty</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-gold">Independent Business Associate</div>
              <div className="font-mono text-sm text-white/90 mt-0.5">{user?.user_code}</div>
            </div>
          </div>

          {/* Body */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-8">
            {/* Photo */}
            <div className="md:col-span-1 flex flex-col items-center gap-3">
              <div className="w-32 h-40 border-2 border-gold/40 bg-ivory rounded-sm overflow-hidden">
                {user?.profile_photo_url ? (
                  <img src={user.profile_photo_url} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-navy/40 text-xs text-center px-2">Photo not uploaded</div>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-navy/50">Rank</div>
              <div className="text-royal font-serif text-lg leading-none">{user?.rank || "Executive"}</div>
            </div>

            {/* Details */}
            <div className="md:col-span-2 space-y-3">
              <Row label="Name" value={user?.name || "—"} />
              <Row label="Associate ID" value={user?.user_code || "—"} mono />
              <Row label="Sponsor" value={user?.sponsor_name || user?.sponsor_code || "—"} />
              <Row label="Mobile" value={user?.phone || "—"} />
              <Row label="Email" value={user?.email || "—"} />
              <Row label="City / State" value={[user?.city, user?.state].filter(Boolean).join(", ") || "—"} />
              <Row label="Joined" value={user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN") : "—"} />

              {/* QR + verification */}
              <div className="pt-4 mt-4 border-t border-line flex items-start gap-4">
                <img src={qrUrl} alt="Verification QR" className="w-24 h-24 bg-white border border-line rounded-sm" />
                <div className="text-[11px] text-navy/60 leading-relaxed">
                  <div className="text-[10px] uppercase tracking-widest text-gold-deep flex items-center gap-1">
                    <ShieldCheck size={11} /> Verify authenticity
                  </div>
                  <div className="mt-1">Scan the QR or visit<br /><span className="text-royal">samarawiz.com/verify/{user?.user_code}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer / issuer block */}
          <div className="bg-ivory border-t border-line px-8 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] text-navy/70">
            <div className="flex items-start gap-2">
              <MapPin size={12} className="text-gold mt-0.5" />
              <span>Samara Wiz Pvt Ltd<br />Vrindavan Garden, House No. 47<br />Ghaziabad (U.P.) 2001009</span>
            </div>
            <div className="flex items-start gap-2">
              <Mail size={12} className="text-gold mt-0.5" />
              <span>support@samarawiz.com<br />info@samarawiz.com</span>
            </div>
            <div className="flex items-start gap-2">
              <Globe size={12} className="text-gold mt-0.5" />
              <span>www.samarawiz.com<br /><span className="text-navy/50">Signature of Authorised Person on behalf of the company</span></span>
            </div>
          </div>
          <div className="bg-royal text-[10px] text-white/70 px-8 py-2 italic">
            Note — The Direct Seller is NOT authorised to collect any type of cash/cheque/demand draft in their name from the customer.
            All payments must be made to Samara Wiz Pvt Ltd via official channels only.
          </div>
        </div>
      </div>

      {/* Print stylesheet — isolates the card and hides dashboard chrome */}
      <style>{`
        @media print {
          nav, header, aside, .sidebar, [data-testid="product-ticker"] { display: none !important; }
          #idcard-print-area { border: 2px solid #0B3D91 !important; box-shadow: none !important; }
          body { background: #fff !important; }
        }
      `}</style>
    </DashboardLayout>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-baseline">
      <div className="text-[10px] uppercase tracking-widest text-navy/50">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? "font-mono" : ""} text-royal font-medium`}>{value}</div>
    </div>
  );
}
